// src/pages/FAQPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, Search, CreditCard, DollarSign, MapPin, Shield, HelpCircle, ArrowRight } from 'lucide-react';

const FAQPage = () => {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const colors = {
    primary: '#2055df',
    primaryDark: '#1a44b8',
    secondary: '#0f172a',
    accent: '#06b6d4',
    success: '#10b981',
    bg: '#f8fafc',
    white: '#ffffff',
    gray100: '#f1f5f9',
    gray200: '#e2e8f0',
    gray400: '#94a3b8',
    gray500: '#64748b',
    gray600: '#475569',
    gray800: '#1e293b',
    gray900: '#0f172a'
  };

  const faqCategories = [
    {
      icon: <CreditCard size={20} />,
      title: 'About the Discount Card',
      faqs: [
        {
          question: 'What is the Rx.ChatWithDr discount card?',
          answer: 'The Rx.ChatWithDr discount card is a free prescription discount program that helps you save up to 80% on prescription medications at over 67,000 pharmacies nationwide. It is NOT insurance — it\'s a discount program that provides access to reduced medication prices at participating pharmacies.'
        },
        {
          question: 'Is the discount card really free?',
          answer: 'Yes, the Rx.ChatWithDr discount card is 100% free. There are no sign-up fees, no monthly charges, and no hidden costs. You can get your digital card instantly by providing your name and email address.'
        },
        {
          question: 'How do I get my discount card?',
          answer: 'Simply visit our Get Card page, enter your name and email, and your digital discount card will be generated instantly. You\'ll also receive a copy via email that you can save to your phone or print out.'
        },
        {
          question: 'Does my card expire?',
          answer: 'No, your Rx.ChatWithDr discount card does not expire. Once you receive it, you can use it indefinitely at any participating pharmacy.'
        },
        {
          question: 'Can my family members use my card?',
          answer: 'Yes! Your discount card can be used by anyone in your household, including family members and pets. There\'s no limit to the number of people who can use a single card.'
        }
      ]
    },
    {
      icon: <DollarSign size={20} />,
      title: 'Pricing & Savings',
      faqs: [
        {
          question: 'How much can I save with the discount card?',
          answer: 'Savings vary by medication and pharmacy, but our users typically save between 30% and 80% off the retail price of their prescriptions. Generic medications tend to offer the highest savings, often 80-90% off retail prices.'
        },
        {
          question: 'Are the prices on the website guaranteed?',
          answer: 'The prices shown on our website are estimates based on current data from our pharmacy network. While they are generally accurate, actual prices may vary slightly at the pharmacy counter. Always confirm the final price with your pharmacist before purchasing.'
        },
        {
          question: 'Why do prices vary between pharmacies?',
          answer: 'Each pharmacy negotiates its own pricing agreements with drug manufacturers and wholesalers. This means the same medication can have different discount prices at different pharmacies. We recommend comparing prices at multiple pharmacies near you to find the best deal.'
        },
        {
          question: 'Can I use the discount card with my insurance?',
          answer: 'You cannot use the discount card and insurance on the same prescription. However, you can compare our discount price with your insurance copay and use whichever option saves you more money. For some medications, especially generics, our discount price may be lower than your insurance copay.'
        },
        {
          question: 'Are there any medications not covered?',
          answer: 'Our discount card covers most FDA-approved prescription medications. However, some controlled substances and specialty medications may not be eligible for discounts. You can search for any medication on our website to check if discounted pricing is available.'
        }
      ]
    },
    {
      icon: <MapPin size={20} />,
      title: 'Pharmacies & Usage',
      faqs: [
        {
          question: 'Which pharmacies accept the discount card?',
          answer: 'The Rx.ChatWithDr discount card is accepted at over 67,000 pharmacies nationwide, including major chains like CVS, Walgreens, Walmart, Rite Aid, Kroger, Costco, and many independent pharmacies. Use our Pharmacy Finder to locate participating pharmacies near you.'
        },
        {
          question: 'How do I use the card at the pharmacy?',
          answer: 'Simply present your digital discount card (on your phone or printed) to the pharmacist when dropping off your prescription. Provide them with the BIN, PCN, Group, and Member ID numbers shown on your card. The pharmacist will process the discount at the register.'
        },
        {
          question: 'What if the pharmacist doesn\'t know how to process it?',
          answer: 'If a pharmacist needs help processing the card, they can call our pharmacy help line at (800) 974-3135. This number is also printed on your discount card for easy reference.'
        },
        {
          question: 'Can I use the card for mail-order pharmacies?',
          answer: 'Our discount card is primarily designed for use at retail pharmacies. Some mail-order pharmacies may accept discount cards, but we recommend contacting the mail-order pharmacy directly to confirm before placing an order.'
        }
      ]
    },
    {
      icon: <Shield size={20} />,
      title: 'Privacy & Security',
      faqs: [
        {
          question: 'Is my personal information safe?',
          answer: 'Yes, we take your privacy seriously. We use industry-standard encryption to protect your personal information. We never sell your data to third parties. Please review our Privacy Policy for complete details on how we handle your information.'
        },
        {
          question: 'What information do you collect?',
          answer: 'We collect minimal information needed to provide our service: your name, email address, and ZIP code for pharmacy searches. We also collect anonymous usage data to improve our platform. You can review our full Privacy Policy for more details.'
        },
        {
          question: 'Can I delete my account and data?',
          answer: 'Yes, you can request deletion of your account and personal data at any time by contacting us at support@chatwithdr.com. We will process your request in accordance with applicable privacy laws.'
        }
      ]
    },
    {
      icon: <HelpCircle size={20} />,
      title: 'General Questions',
      faqs: [
        {
          question: 'Is Rx.ChatWithDr insurance?',
          answer: 'No, Rx.ChatWithDr is NOT insurance. It is a prescription discount program. It cannot be used in combination with insurance for the same prescription. If you have insurance, compare our discount price with your copay to determine which option saves you more.'
        },
        {
          question: 'Do I need a prescription to use the card?',
          answer: 'Yes, you still need a valid prescription from a licensed healthcare provider. The discount card only helps reduce the cost of your prescription — it does not replace the need for a prescription. If you need a prescription, visit ChatWithDr.com for affordable online doctor consultations starting at $39.99.'
        },
        {
          question: 'How is this different from GoodRx or other discount cards?',
          answer: 'Like other prescription discount programs, we help you save on medications. Our card is completely free with no premium tiers, and we\'re integrated with ChatWithDr.com\'s telehealth platform, making it easy to get both a prescription and savings in one place.'
        },
        {
          question: 'Who do I contact for help?',
          answer: 'For general questions, email us at support@chatwithdr.com or call (919) 588-2428. For pharmacy processing issues, pharmacists can call our dedicated pharmacy help line at (800) 974-3135.'
        },
        {
          question: 'Is the program available in all states?',
          answer: 'Yes, the Rx.ChatWithDr discount card is available in all 50 states and can be used at any participating pharmacy nationwide.'
        }
      ]
    }
  ];

  // Flatten all FAQs for search
  const allFaqs = faqCategories.flatMap((cat, catIndex) =>
    cat.faqs.map((faq, faqIndex) => ({
      ...faq,
      category: cat.title,
      globalIndex: `${catIndex}-${faqIndex}`
    }))
  );

  const filteredFaqs = searchQuery.trim()
    ? allFaqs.filter(
        faq =>
          faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : null;

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const FAQItem = ({ question, answer, index, isOpen, onToggle }) => (
    <div
      style={{
        background: colors.white,
        borderRadius: 12,
        border: `1px solid ${isOpen ? colors.primary + '30' : colors.gray200}`,
        marginBottom: 10,
        overflow: 'hidden',
        transition: 'all 0.2s'
      }}
    >
      <button
        onClick={onToggle}
        style={{
          width: '100%',
          padding: isMobile ? '16px 16px' : '20px 24px',
          background: isOpen ? `${colors.primary}06` : 'transparent',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          textAlign: 'left'
        }}
      >
        <span style={{
          fontSize: isMobile ? 15 : 16,
          fontWeight: 600,
          color: isOpen ? colors.primary : colors.gray800,
          lineHeight: 1.4
        }}>
          {question}
        </span>
        <span style={{ flexShrink: 0, color: isOpen ? colors.primary : colors.gray400 }}>
          {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </span>
      </button>
      {isOpen && (
        <div style={{
          padding: isMobile ? '0 16px 16px' : '0 24px 20px',
          color: colors.gray600,
          fontSize: isMobile ? 14 : 15,
          lineHeight: 1.7
        }}>
          {answer}
        </div>
      )}
    </div>
  );

  return (
    <div style={{
      fontFamily: "'Inter', -apple-system, sans-serif",
      background: colors.bg,
      minHeight: '100vh'
    }}>
      {/* Hero Section */}
      <section style={{
        background: colors.primary,
        padding: isMobile ? '48px 16px 56px' : '80px 24px 100px',
        textAlign: 'center',
        marginTop: 0
      }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <h1 style={{
            fontSize: isMobile ? 28 : 48,
            fontWeight: 800,
            color: 'white',
            marginBottom: 14,
            lineHeight: 1.2
          }}>
            Frequently Asked Questions
          </h1>
          <p style={{
            fontSize: isMobile ? 15 : 20,
            color: 'rgba(255,255,255,0.85)',
            marginBottom: isMobile ? 28 : 40,
            lineHeight: 1.5
          }}>
            Everything you need to know about your free prescription discount card
          </p>

          {/* Search Box */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: 'white',
            borderRadius: 12,
            padding: '4px 16px',
            maxWidth: 500,
            margin: '0 auto',
            boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
          }}>
            <Search size={20} style={{ color: colors.gray400, flexShrink: 0 }} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setOpenIndex(null); }}
              placeholder="Search questions..."
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                fontSize: 16,
                padding: '14px 0',
                background: 'transparent',
                color: colors.gray800,
                minWidth: 0
              }}
            />
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section style={{ padding: isMobile ? '32px 16px' : '60px 24px' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>

          {/* Search Results */}
          {filteredFaqs ? (
            <div>
              <p style={{
                fontSize: 14,
                color: colors.gray500,
                marginBottom: 20
              }}>
                {filteredFaqs.length} result{filteredFaqs.length !== 1 ? 's' : ''} for "{searchQuery}"
              </p>
              {filteredFaqs.length > 0 ? (
                filteredFaqs.map((faq, i) => (
                  <FAQItem
                    key={faq.globalIndex}
                    question={faq.question}
                    answer={faq.answer}
                    index={faq.globalIndex}
                    isOpen={openIndex === faq.globalIndex}
                    onToggle={() => toggleFaq(faq.globalIndex)}
                  />
                ))
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: isMobile ? 40 : 60,
                  background: colors.white,
                  borderRadius: 16,
                  border: `1px solid ${colors.gray200}`
                }}>
                  <HelpCircle size={40} style={{ color: colors.gray400, marginBottom: 16 }} />
                  <h3 style={{ fontSize: 18, fontWeight: 600, color: colors.gray800, marginBottom: 8 }}>
                    No results found
                  </h3>
                  <p style={{ color: colors.gray500, fontSize: 15, marginBottom: 20 }}>
                    Try a different search term or browse the categories below
                  </p>
                  <button
                    onClick={() => setSearchQuery('')}
                    style={{
                      padding: '10px 24px',
                      background: colors.primary,
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Clear Search
                  </button>
                </div>
              )}
            </div>
          ) : (
            /* Category-based FAQs */
            faqCategories.map((category, catIndex) => (
              <div key={catIndex} style={{ marginBottom: isMobile ? 36 : 48 }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 16
                }}>
                  <div style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: `${colors.primary}10`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: colors.primary
                  }}>
                    {category.icon}
                  </div>
                  <h2 style={{
                    fontSize: isMobile ? 18 : 20,
                    fontWeight: 700,
                    color: colors.gray900,
                    margin: 0
                  }}>
                    {category.title}
                  </h2>
                </div>
                {category.faqs.map((faq, faqIndex) => {
                  const globalIndex = `${catIndex}-${faqIndex}`;
                  return (
                    <FAQItem
                      key={globalIndex}
                      question={faq.question}
                      answer={faq.answer}
                      index={globalIndex}
                      isOpen={openIndex === globalIndex}
                      onToggle={() => toggleFaq(globalIndex)}
                    />
                  );
                })}
              </div>
            ))
          )}
        </div>
      </section>

      {/* Still Have Questions CTA */}
      <section style={{
        background: 'white',
        padding: isMobile ? '40px 16px' : '60px 24px',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <h2 style={{
            fontSize: isMobile ? 20 : 24,
            fontWeight: 700,
            color: colors.gray900,
            marginBottom: 12
          }}>
            Still Have Questions?
          </h2>
          <p style={{
            fontSize: isMobile ? 15 : 16,
            color: colors.gray600,
            marginBottom: 24,
            lineHeight: 1.6
          }}>
            Our support team is here to help. Reach out and we'll get back to you as soon as possible.
          </p>
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 12,
            justifyContent: 'center'
          }}>
            <a
              href="mailto:support@chatwithdr.com"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: isMobile ? '14px 24px' : '14px 28px',
                background: colors.primary,
                color: 'white',
                border: 'none',
                borderRadius: 10,
                fontSize: isMobile ? 15 : 16,
                fontWeight: 600,
                textDecoration: 'none',
                cursor: 'pointer'
              }}
            >
              Email Support <ArrowRight size={18} />
            </a>
            <button
              onClick={() => navigate('/card')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: isMobile ? '14px 24px' : '14px 28px',
                background: 'transparent',
                color: colors.primary,
                border: `2px solid ${colors.primary}`,
                borderRadius: 10,
                fontSize: isMobile ? 15 : 16,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              <CreditCard size={18} /> Get Free Card
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;